import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { Input, Modal, Table, Tag } from '@arco-design/web-react'
import { IconSearch } from '@arco-design/web-react/icon'
import type { RatioSyncChannel } from '@/api/ratioSync'

const OFFICIAL_RATIO_PRESET_ID = -100
const MODELS_DEV_PRESET_ID = -101
const OFFICIAL_RATIO_PRESET_NAME = '官方倍率预设'
const MODELS_DEV_PRESET_NAME = 'models.dev 价格预设'
const OFFICIAL_RATIO_PRESET_BASE_URL = 'https://basellm.github.io'
const MODELS_DEV_PRESET_BASE_URL = 'https://models.dev'

export interface ChannelTransferItem {
  key: number
  label: string
  value: number
  disabled: boolean
  _originalData: RatioSyncChannel
}

export interface ChannelSelectorModalRef {
  resetPagination: () => void
}

interface Props {
  visible: boolean
  onCancel: () => void
  onOk: () => void
  allChannels: ChannelTransferItem[]
  selectedChannelIds: number[]
  setSelectedChannelIds: (ids: number[]) => void
  channelEndpoints: Record<number, string>
  updateChannelEndpoint: (channelId: number, endpoint: string) => void
}

function isOfficialChannel(record: ChannelTransferItem) {
  const id = record.key
  const base = record._originalData?.base_url || ''
  const name = record.label || ''
  return (
    id === OFFICIAL_RATIO_PRESET_ID ||
    id === MODELS_DEV_PRESET_ID ||
    base === OFFICIAL_RATIO_PRESET_BASE_URL ||
    base === MODELS_DEV_PRESET_BASE_URL ||
    name === OFFICIAL_RATIO_PRESET_NAME ||
    name === MODELS_DEV_PRESET_NAME
  )
}

const ChannelSelectorModal = forwardRef<ChannelSelectorModalRef, Props>(function ChannelSelectorModal(
  {
    visible,
    onCancel,
    onOk,
    allChannels,
    selectedChannelIds,
    setSelectedChannelIds,
    channelEndpoints,
    updateChannelEndpoint,
  },
  ref
) {
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  useImperativeHandle(ref, () => ({
    resetPagination: () => {
      setCurrentPage(1)
      setSearchText('')
    },
  }))

  const filteredData = useMemo(() => {
    const searchLower = searchText.trim().toLowerCase()
    const matched = searchLower
      ? allChannels.filter((item) => {
          const name = (item.label || '').toLowerCase()
          const baseUrl = (item._originalData?.base_url || '').toLowerCase()
          return name.includes(searchLower) || baseUrl.includes(searchLower)
        })
      : allChannels
    return [...matched].sort((a, b) => {
      const wa = isOfficialChannel(a) ? 0 : 1
      const wb = isOfficialChannel(b) ? 0 : 1
      return wa - wb
    })
  }, [allChannels, searchText])

  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const columns = [
    {
      title: '渠道',
      dataIndex: 'label',
      render: (_: unknown, record: ChannelTransferItem) => (
        <div>
          <div>{record.label}</div>
          <div className="text-xs text-slate-500">{record._originalData?.base_url || '-'}</div>
        </div>
      ),
    },
    {
      title: '端点',
      render: (_: unknown, record: ChannelTransferItem) => (
        <Input
          size="small"
          value={channelEndpoints[record.key] ?? ''}
          onChange={(value) => updateChannelEndpoint(record.key, value)}
        />
      ),
    },
    {
      title: '类型',
      width: 100,
      render: (_: unknown, record: ChannelTransferItem) =>
        isOfficialChannel(record) ? <Tag color="arcoblue">预设</Tag> : <Tag>渠道</Tag>,
    },
  ]

  return (
    <Modal title="选择同步渠道" visible={visible} onCancel={onCancel} onOk={onOk} style={{ width: 860 }}>
      <Input
        prefix={<IconSearch />}
        placeholder="搜索渠道名称或 Base URL"
        value={searchText}
        onChange={setSearchText}
        allowClear
        className="!mb-3"
      />
      <Table
        rowKey="key"
        columns={columns}
        data={paginatedData}
        rowSelection={{
          selectedRowKeys: selectedChannelIds,
          onChange: (keys) => setSelectedChannelIds(keys as number[]),
        }}
        pagination={{
          current: currentPage,
          pageSize,
          total: filteredData.length,
          onChange: setCurrentPage,
        }}
      />
    </Modal>
  )
})

export default ChannelSelectorModal

export {
  OFFICIAL_RATIO_PRESET_ID,
  MODELS_DEV_PRESET_ID,
  OFFICIAL_RATIO_PRESET_NAME,
  MODELS_DEV_PRESET_NAME,
  OFFICIAL_RATIO_PRESET_BASE_URL,
  MODELS_DEV_PRESET_BASE_URL,
}
